import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import FilteringHeader from 'components/FilteringHeader';
import Filter from 'features/resultFiltering/Filter';
import {
  changeFilter,
  clearFilters,
  Extents,
  FilterValues,
  getExtentsOf,
  getFilters,
  getSlidersExtents,
  initialiseFilters,
} from 'features/resultFiltering/resultFilteringSlice';
import { inFilter } from 'features/searchMolecules/ResultsView/utils';
import { SearchResultsGroupMember } from 'features/searchMolecules/searchMoleculesSlice';
import { useThunkDispatch } from 'hooks/hooks';
import partition from 'lodash/partition';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Props as FilterProps } from './Filter';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: { padding: theme.spacing(2) },
  }),
);

interface Props {
  closeFilterPanel: () => void;
  activeCalcs: string[];
  calcsLookup: { [key: string]: string };
  selectedResultMembers: SearchResultsGroupMember[];
  selectedResultChanged: boolean;
}

const Filtering = ({
  closeFilterPanel,
  activeCalcs,
  calcsLookup,
  selectedResultMembers,
  selectedResultChanged,
}: Props) => {
  const classes = useStyles();
  const { t } = useTranslation(['common', 'results']);

  const filters = useSelector(getFilters);
  const dispatch = useThunkDispatch();

  // Update the filters extents when the selected result changes
  const sliderExtents = useRef<Extents>(
    getSlidersExtents(selectedResultMembers, activeCalcs, filters),
  );
  if (selectedResultChanged) {
    sliderExtents.current = {
      ...sliderExtents.current,
      ...getSlidersExtents(selectedResultMembers, activeCalcs, filters),
    };
  }

  const handleClearFilters = () => {
    dispatch(clearFilters());
    dispatch(initialiseFilters({ activeCalcs, selectedResultMembers }));

    // Set min/max of sliders to range of data ignoring current values
    sliderExtents.current = {
      ...sliderExtents.current,
      ...getExtentsOf(selectedResultMembers, activeCalcs),
    };
  };

  const data = partition(selectedResultMembers, (member) => inFilter(member, filters));
  let filtersProps: FilterProps[] = activeCalcs.map((name) => {
    const {
      [name]: { min: sliderMin, max: sliderMax },
    } = sliderExtents.current;
    const {
      [name]: { min: resultsMin, max: resultsMax },
    } = getExtentsOf(selectedResultMembers, activeCalcs);

    const {
      [name]: { values, lowerChanged, upperChanged },
    } = filters;

    // Get the values for the ticks that show the range of the current results data

    const description = calcsLookup[name];

    const numFiltered =
      selectedResultMembers.length -
      selectedResultMembers.filter((member) => inFilter(member, { [name]: filters[name] })).length;

    return {
      property: { name, description },
      sliderMin,
      sliderMax,
      resultsMin,
      resultsMax,
      data,
      numFiltered,
      lowerChanged,
      upperChanged,
      initialValues: values,
      changeValues: (newValues: FilterValues) => {
        dispatch(changeFilter({ name, newValues }));
      },
    };
  });

  if (activeCalcs.length && Object.keys(filters).length) {
    return (
      <div className={classes.root}>
        <FilteringHeader
          handleClearFilters={handleClearFilters}
          closeFilterPanel={closeFilterPanel}
        />
        {filtersProps.map((props, idx) => {
          console.log(props);

          return <Filter key={idx} {...props} />;
        })}
      </div>
    );
  } else {
    // No calcs so no filters
    return <div>{t('results:no_calcs')}</div>;
  }
};

export default Filtering;
